import type { CourseSession } from "./api";

export type YearSessionDensity = Record<string, number>;

function dateKey(date: Date): string {
  const pad = (value: number) => String(value).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

function localDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

/**
 * Counts non-cancelled sessions for every local calendar day in a year.
 * Session end times are exclusive, so a session ending exactly at midnight
 * does not add density to the following day.
 */
export function getYearSessionDensity(year: number, sessions: readonly CourseSession[]): YearSessionDensity {
  const density: YearSessionDensity = {};
  const firstDay = new Date(year, 0, 1);
  const lastDay = new Date(year, 11, 31);

  for (const day = new Date(firstDay); day <= lastDay; day.setDate(day.getDate() + 1)) {
    density[dateKey(day)] = 0;
  }

  for (const session of sessions) {
    if (session.status === "storniert") continue;

    const start = new Date(session.plannedStart);
    const end = new Date(session.plannedEnd);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || end <= start) continue;

    const startDay = localDay(start);
    // The end of a FullCalendar event is exclusive. Subtracting one millisecond
    // gives the final day actually occupied by this session.
    const endDay = localDay(new Date(end.getTime() - 1));
    const from = startDay < firstDay ? firstDay : startDay;
    const to = endDay > lastDay ? lastDay : endDay;

    for (const day = new Date(from); day <= to; day.setDate(day.getDate() + 1)) {
      const key = dateKey(day);
      density[key] = (density[key] ?? 0) + 1;
    }
  }

  return density;
}
